import React, { useCallback, useMemo } from "react";
import type { ReactElement, ComponentProps } from "react";

import { HStack, Tooltip } from "native-base";

import Cell from "pianofunclub-shared/components/Base/Cell";
import type { CellProps } from "pianofunclub-shared/components/Base/Cell";

type HStackProps = ComponentProps<typeof HStack>;

export type TableData = (
  | {
      cellProps?: CellProps;
      data: string | number | ReactElement;
      icon?: React.JSX.Element;
      isPlaceholder?: boolean;
      onPress?: (data?: string) => void;
      tooltipLabel?: string;
    }
  | null
  | undefined
)[];

interface Props extends HStackProps {
  cellProps?: CellProps;
  data: TableData;
  flexArray: number[];
  rowHeight?: number | string;
  rowIndex: number;
  tableBorderColor?: string;
  tableBorderWidth?: number;
  tooltipLabel?: string;
}

const Row = (props: Props): ReactElement => {
  const {
    cellProps,
    data,
    flexArray,
    rowHeight,
    rowIndex,
    tableBorderColor,
    tableBorderWidth,
    tooltipLabel,
  } = props;

  const filteredData = useMemo(
    () => data.filter((data) => data !== null && data !== undefined),
    [data]
  );

  const renderRow = useCallback(() => {
    return (
      <HStack {...props}>
        {filteredData.map((item, index) => {
          return (
            <Cell
              key={index}
              data={item.data}
              flex={flexArray[index]}
              height={rowHeight}
              icon={item?.icon}
              isFinal={filteredData.length === index + 1}
              isPlaceholder={item?.isPlaceholder}
              onPress={item?.onPress}
              rowIndex={rowIndex}
              tableBorderColor={tableBorderColor}
              tableBorderWidth={tableBorderWidth}
              tooltipLabel={item?.tooltipLabel}
              {...cellProps}
              {...item.cellProps}
            />
          );
        })}
      </HStack>
    );
  }, [
    cellProps,
    filteredData,
    flexArray,
    props,
    rowHeight,
    rowIndex,
    tableBorderColor,
    tableBorderWidth,
  ]);

  if (tooltipLabel) {
    return (
      <Tooltip label={tooltipLabel} placement="bottom" pointerEvents="none">
        {renderRow()}
      </Tooltip>
    );
  }

  return renderRow();
};

export default React.memo(Row);
